import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ChatMessageData } from "./types";
import { generateImage } from "../../api/generateImage";

interface UseImageGenerationParams {
  setMessages: React.Dispatch<React.SetStateAction<ChatMessageData[]>>;
  disableContext: boolean;
}

interface UseImageGenerationReturn {
  isGenerating: boolean;
  handleSendMessage: (prompt: string, referenceImage: string | null) => Promise<boolean>;
}

/**
 * Hook to manage image generation flow
 * Handles creating user/AI messages, calling the generation API, and updating message states
 */
export const useImageGeneration = ({ setMessages, disableContext }: UseImageGenerationParams): UseImageGenerationReturn => {
  const { t } = useTranslation();
  const [isGenerating, setIsGenerating] = useState<boolean>(false);

  // Helper to update a specific AI message with new properties
  const updateAiMessage = (aiMessageId: string, updates: Partial<ChatMessageData>) => {
    setMessages(prev =>
      prev.map(msg => (msg.id === aiMessageId ? { ...msg, ...updates } : msg))
    );
  };

  const handleSendMessage = async (prompt: string, referenceImage: string | null): Promise<boolean> => {
    if (!prompt.trim() || isGenerating) return false;

    setIsGenerating(true);

    // Reference images are passed as data URLs from ChatInput and can be stored/displayed directly
    const userMessageId = `user-${Date.now()}`;
    const userMessage: ChatMessageData = {
      id: userMessageId,
      role: "user",
      timestamp: Date.now(),
      content: prompt,
      referenceImageUrl: referenceImage || undefined,
      status: "completed",
    };

    // Create pending AI message
    const aiMessageId = `ai-${Date.now()}`;
    const aiMessage: ChatMessageData = {
      id: aiMessageId,
      role: "assistant",
      timestamp: Date.now(),
      status: "pending",
    };

    // Add both messages to state
    setMessages(prev => [...prev, userMessage, aiMessage]);

    try {
      const blob = await generateImage({
        prompt,
        base64Image: referenceImage || undefined,
        disableContext,
      });

      if (blob) {
        const objectUrl = URL.createObjectURL(blob);
        updateAiMessage(aiMessageId, {
          status: "completed" as const,
          imageUrl: objectUrl,
          imageBlob: blob,
        });
        return true; // Success
      } else {
        updateAiMessage(aiMessageId, {
          status: "error" as const,
          error: t("imageGenerator.errorGenerating"),
        });
        return false; // Failed
      }
    } catch (err) {
      console.error(err);
      const errorMessage = err instanceof Error ? err.message : t("imageGenerator.errorGenerating");
      updateAiMessage(aiMessageId, {
        status: "error" as const,
        error: errorMessage,
      });
      return false; // Failed
    } finally {
      setIsGenerating(false);
    }
  };

  return {
    isGenerating,
    handleSendMessage,
  };
};
